// Single source of truth for all public-page SEO metadata and JSON-LD schemas.
// Imported by both page components (runtime) and prerender.tsx (build-time).
// Keep this file Node-safe: no browser APIs, no React imports.

import { socialUrls } from './socials';

export interface RouteSEOConfig {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage: string;
  robots?: string;
  structuredData?: object[];
}

// ─── JSON-LD schemas ──────────────────────────────────────────────────────────

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Joylo",
  "applicationCategory": "DeveloperApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Free tier with 30 monthly credits",
  },
  "description": "AI-powered app development platform with certified human engineers for production deployment.",
  "url": "https://joylo.ai",
};

export const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "Joylo",
  "description": "AI app builder with human engineer backup.",
  "brand": { "@type": "Brand", "name": "Joylo" },
  "offers": {
    "@type": "AggregateOffer",
    "priceCurrency": "USD",
    "lowPrice": "0",
    "highPrice": "100",
    "offerCount": "4",
    "offers": [
      { "@type": "Offer", "name": "Free", "price": "0", "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Solo", "price": "25", "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Starter", "price": "50", "priceCurrency": "USD" },
      { "@type": "Offer", "name": "Pro", "price": "100", "priceCurrency": "USD" },
    ],
  },
};

export const pricingBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Pricing", "item": "https://joylo.ai/pricing" },
  ],
};

export const productionGuaranteeBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Production Guarantee", "item": "https://joylo.ai/production-guarantee" },
  ],
};

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Joylo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Joylo is an AI-powered platform that turns natural language prompts into production-ready full-stack applications. Describe what you want to build; our AI plans, designs, generates, and deploys it to Kubernetes automatically.",
      },
    },
    {
      "@type": "Question",
      "name": "How does Expert Assist work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Expert Assist is a $500 add-on that gives you 10 hours of certified architect time to handle production tasks AI cannot finish reliably.",
      },
    },
    {
      "@type": "Question",
      "name": "What plans are available?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Joylo offers Free ($0/mo), Solo ($25/mo), Starter ($50/mo), and Pro ($100/mo) plans. Expert Assist ($500) adds 10 hours of certified architect time. See the Pricing page for full details.",
      },
    },
    {
      "@type": "Question",
      "name": "Can I export my generated code?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. You can download or connect a GitHub repository to your Joylo project at any time to access the full source code.",
      },
    },
  ],
};

export const helpBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
  ],
};

// Article-level breadcrumb schemas (3 levels: Home → Help → Article)
export const aiModelsBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
    { "@type": "ListItem", "position": 3, "name": "AI Models", "item": "https://joylo.ai/help/articles/ai-models" },
  ],
};

export const appSleepingBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
    { "@type": "ListItem", "position": 3, "name": "App Sleeping", "item": "https://joylo.ai/help/articles/app-sleeping" },
  ],
};

export const cancelDowngradeBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
    { "@type": "ListItem", "position": 3, "name": "Cancel or Downgrade", "item": "https://joylo.ai/help/articles/cancel-downgrade" },
  ],
};

export const customDomainBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
    { "@type": "ListItem", "position": 3, "name": "Custom Domain", "item": "https://joylo.ai/help/articles/custom-domain" },
  ],
};

export const howCreditsWorkBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
    { "@type": "ListItem", "position": 3, "name": "How Credits Work", "item": "https://joylo.ai/help/articles/how-credits-work" },
  ],
};

export const joyloArchitectBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://joylo.ai/" },
    { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://joylo.ai/help" },
    { "@type": "ListItem", "position": 3, "name": "Joylo Architect", "item": "https://joylo.ai/help/articles/joylo-architect" },
  ],
};

// sameAs is what ties the social profiles to the brand entity for search and AI-search crawlers.
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Joylo",
  "url": "https://joylo.ai",
  "logo": "https://joylo.ai/og/logo.png",
  "description": "AI app builder with certified human engineers built in.",
  "sameAs": socialUrls,
};

// ─── Per-route SEO configuration ─────────────────────────────────────────────

const DEFAULT_OG_IMAGE = 'https://joylo.ai/og/logo.png';

export const ROUTE_SEO: Record<string, RouteSEOConfig> = {
  '/': {
    title: 'Joylo: AI App Builder with Human Engineers Built In',
    description: 'Joylo is the only AI app builder with real engineers behind you, whether you\'re stuck building, launching to production, or once real users show up.',
    canonicalUrl: 'https://joylo.ai/',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [softwareApplicationSchema, organizationSchema],
  },
  '/pricing': {
    title: 'Pricing: Free to $100/mo + Expert Assist | Joylo',
    description: 'Joylo pricing: free tier with 30 credits, Solo $25, Starter $50, Pro $100. Add Expert Assist for $500 to get 10 hours of certified architect time.',
    canonicalUrl: 'https://joylo.ai/pricing',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [productSchema, pricingBreadcrumbSchema],
  },
  '/production-guarantee': {
    title: 'Joylo Production Guarantee | We Get Your App Live',
    description: 'Agree the project scope and purchase the recommended engineering hours. Joylo gets that scope live and carries engineering overruns, subject to the stated conditions.',
    canonicalUrl: 'https://joylo.ai/production-guarantee',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [productionGuaranteeBreadcrumbSchema],
  },
  '/help': {
    title: 'Help Center: Docs, Guides and FAQs | Joylo',
    description: 'Get help with Joylo. Step-by-step guides for building, deploying, and scaling apps. Docs on credits, plans, Expert Assist, and Co-Build.',
    canonicalUrl: 'https://joylo.ai/help',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [faqSchema, helpBreadcrumbSchema],
  },
  '/help/articles/how-credits-work': {
    title: 'How Credits Work — Joylo Help',
    description: 'Learn how Joylo credits work, how much each action costs, and what happens when credits run out.',
    canonicalUrl: 'https://joylo.ai/help/articles/how-credits-work',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [howCreditsWorkBreadcrumb],
  },
  '/help/articles/custom-domain': {
    title: 'How to Deploy with a Custom Domain — Joylo Help',
    description: 'Step-by-step guide to connecting your own domain to a Joylo-deployed app.',
    canonicalUrl: 'https://joylo.ai/help/articles/custom-domain',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [customDomainBreadcrumb],
  },
  '/help/articles/joylo-architect': {
    title: 'What is a Joylo Architect? — Joylo Help',
    description: "Learn about Joylo Architects — certified senior engineers who work alongside AI on your project.",
    canonicalUrl: 'https://joylo.ai/help/articles/joylo-architect',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [joyloArchitectBreadcrumb],
  },
  '/help/articles/cancel-downgrade': {
    title: 'How to Cancel or Downgrade Your Plan — Joylo Help',
    description: 'How to cancel or downgrade your Joylo subscription with no fees or lock-in.',
    canonicalUrl: 'https://joylo.ai/help/articles/cancel-downgrade',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [cancelDowngradeBreadcrumb],
  },
  '/help/articles/app-sleeping': {
    title: "App is Sleeping / Not Responding — Joylo Help",
    description: "Why Joylo apps sleep and how to fix an app that isn't responding.",
    canonicalUrl: 'https://joylo.ai/help/articles/app-sleeping',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [appSleepingBreadcrumb],
  },
  '/help/articles/ai-models': {
    title: 'What AI Models Does Joylo Use? — Joylo Help',
    description: "Learn which AI models power Joylo's code generation, design, and planning capabilities.",
    canonicalUrl: 'https://joylo.ai/help/articles/ai-models',
    ogImage: DEFAULT_OG_IMAGE,
    structuredData: [aiModelsBreadcrumb],
  },
};
